import { useState, useEffect } from "react";
import Navigation from "@/components/Navigation";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  image_url?: string;
}

const featuredProducts = [
  {
    id: "featured-1",
    name: "Bio Cleanse",
    description: "Deep cleansing formula for all skin types",
    price: 45.99,
    category: "Skincare",
    image_url: "/lovable-uploads/ffa3aa97-5c0d-4dde-8b02-6aaaa5a263cc.png"
  },
  {
    id: "featured-2",
    name: "Lock Treatment Set",
    description: "Complete hair treatment system",
    price: 129.99,
    category: "Hair Care",
    image_url: "/lovable-uploads/d333c4a1-52dc-4905-bd50-5c598f00d849.png"
  }
];

const Store = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('name');

      if (error) throw error;
      
      // Combine featured products with database products
      const allProducts = [...featuredProducts, ...(data || [])];
      setProducts(allProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load products. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = async (product: Product) => {
    try {
      const response = await supabase.functions.invoke('create-checkout', {
        body: { productId: product.name, price: product.price },
      });

      if (response.error) throw response.error;
      
      if (response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to initiate checkout. Please try again later.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="pt-24 px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-serif mb-6">Our Store</h1>
          
          {loading ? (
            <p className="text-center py-8">Loading products...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <Card key={product.id} className="flex flex-col">
                  <CardHeader>
                    {product.image_url && (
                      <div className="w-full h-64 mb-4 overflow-hidden rounded-lg">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover object-center transform hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <CardTitle className="text-xl">{product.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <p className="text-gray-600 mb-4">{product.description}</p>
                    <p className="text-lg font-semibold text-primary">
                      ${product.price.toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Category: {product.category}
                    </p>
                  </CardContent>
                  <CardFooter>
                    <Button 
                      className="w-full"
                      onClick={() => handleBuyNow(product)}
                    >
                      Buy Now
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Store;